import { ChartEntry, PriceHistoryPoint, ZoomLevels } from './types'

const DOMAIN_PADDING_EXPONENT = 0.08
export const LIQUIDITY_BUCKET_COUNT = 20

const TOKEN_COLOR_PALETTE = ['#F7931A', '#627EEA', '#26A17B', '#A457FF', '#E84142', '#2775CA', '#FC72FF', '#21B99A']

const isPositiveFinite = (value: number | undefined): value is number =>
  value !== undefined && Number.isFinite(value) && value > 0

const hashTokenKey = (key: string): number => {
  let hash = 0
  for (let index = 0; index < key.length; index++) {
    hash = (hash * 31 + key.charCodeAt(index)) >>> 0
  }
  return hash
}

const getKnownTokenColor = (symbol?: string): string | undefined => {
  const normalized = symbol?.toUpperCase()
  if (!normalized) return undefined
  if (normalized.includes('BTC')) return '#F7931A'
  if (normalized === 'ETH' || normalized === 'WETH' || normalized.endsWith('ETH')) return '#627EEA'
  if (normalized.includes('USDC')) return '#2775CA'
  if (normalized.includes('USDT')) return '#26A17B'
  if (normalized.includes('DAI')) return '#F5AC37'
  if (normalized.includes('CRO')) return '#002D74'
  return undefined
}

export function getTokenColorFallbacks(
  tokenA: { key?: string; symbol?: string },
  tokenB: { key?: string; symbol?: string }
): [string, string] {
  const first =
    getKnownTokenColor(tokenA.symbol) ??
    TOKEN_COLOR_PALETTE[hashTokenKey(tokenA.key ?? tokenA.symbol ?? 'token-a') % TOKEN_COLOR_PALETTE.length]
  let second =
    getKnownTokenColor(tokenB.symbol) ??
    TOKEN_COLOR_PALETTE[hashTokenKey(tokenB.key ?? tokenB.symbol ?? 'token-b') % TOKEN_COLOR_PALETTE.length]

  if (second.toLowerCase() === first.toLowerCase()) {
    const firstIndex = TOKEN_COLOR_PALETTE.findIndex((color) => color.toLowerCase() === first.toLowerCase())
    second = TOKEN_COLOR_PALETTE[(Math.max(0, firstIndex) + 3) % TOKEN_COLOR_PALETTE.length]
  }

  return [first, second]
}

/**
 * Reorients active-liquidity intervals into the displayed price direction.
 * When prices are inverted, the liquidity at each reversed boundary belongs
 * to the next ascending boundary rather than the boundary it came from.
 */
export function orientLiquiditySeries(series: ChartEntry[], invertPrice: boolean): ChartEntry[] {
  const oriented = series
    .map((entry) => ({
      activeLiquidity: Math.max(0, entry.activeLiquidity),
      price0: invertPrice ? 1 / entry.price0 : entry.price0,
    }))
    .filter((entry) => isPositiveFinite(entry.price0) && Number.isFinite(entry.activeLiquidity))
    .sort((first, second) => first.price0 - second.price0)

  if (!invertPrice) return oriented

  return oriented.map((entry, index) => ({
    ...entry,
    activeLiquidity: oriented[index + 1]?.activeLiquidity ?? 0,
  }))
}

export function getChartPriceDomain({
  current,
  history,
  brushDomain,
  zoomLevels,
}: {
  current: number
  history: PriceHistoryPoint[]
  brushDomain?: [number, number]
  zoomLevels: ZoomLevels
}): [number, number] {
  const safeCurrent = isPositiveFinite(current) ? current : 1
  const values = [safeCurrent * zoomLevels.initialMin, safeCurrent * zoomLevels.initialMax, safeCurrent]

  for (const point of history) {
    if (isPositiveFinite(point.price)) {
      values.push(point.price)
    }
  }

  // Full-range positions use protocol-limit prices that would flatten the useful
  // viewport. Include custom bounds, but leave protocol-limit bounds off-screen.
  const minimumVisibleBound = safeCurrent * zoomLevels.min
  const maximumVisibleBound = safeCurrent * zoomLevels.max
  for (const bound of brushDomain ?? []) {
    if (isPositiveFinite(bound) && bound >= minimumVisibleBound && bound <= maximumVisibleBound) {
      values.push(bound)
    }
  }

  let minimum = Math.min(...values)
  let maximum = Math.max(...values)

  if (minimum === maximum) {
    minimum *= 0.999
    maximum *= 1.001
  }

  const ratio = maximum / minimum
  const paddingFactor = Math.max(Math.pow(ratio, DOMAIN_PADDING_EXPONENT), 1.01)

  return [minimum / paddingFactor, maximum * paddingFactor]
}

interface LiquidityBucket {
  activeLiquidity: number
  index: number
  price: number
}

/**
 * Samples the piecewise-active liquidity profile into fixed visual rows.
 * Uniswap's current chart uses twenty visible buckets so sparse or dense pools
 * remain readable at every zoom level.
 */
export function buildLiquidityBuckets(
  series: ChartEntry[],
  priceDomain: [number, number],
  bucketCount = LIQUIDITY_BUCKET_COUNT
): LiquidityBucket[] {
  if (bucketCount < 1 || !isPositiveFinite(priceDomain[0]) || !isPositiveFinite(priceDomain[1])) {
    return []
  }

  const [minimum, maximum] = priceDomain[0] <= priceDomain[1] ? priceDomain : [priceDomain[1], priceDomain[0]]
  const sortedSeries = series
    .filter((entry) => isPositiveFinite(entry.price0) && Number.isFinite(entry.activeLiquidity))
    .sort((first, second) => first.price0 - second.price0)

  if (!sortedSeries.length) {
    return []
  }

  const logMinimum = Math.log(minimum)
  const logMaximum = Math.log(maximum)

  return Array.from({ length: bucketCount }, (_, index) => {
    // Index zero is the top of the chart (highest visible price).
    const position = (index + 0.5) / bucketCount
    const price = Math.exp(logMaximum + (logMinimum - logMaximum) * position)

    let low = 0
    let high = sortedSeries.length - 1
    let activeIndex = -1

    while (low <= high) {
      const middle = Math.floor((low + high) / 2)
      if (sortedSeries[middle].price0 <= price) {
        activeIndex = middle
        low = middle + 1
      } else {
        high = middle - 1
      }
    }

    return {
      activeLiquidity: activeIndex >= 0 ? Math.max(0, sortedSeries[activeIndex].activeLiquidity) : 0,
      index,
      price,
    }
  }).filter((bucket) => bucket.activeLiquidity > 0)
}
